import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiCellphoneKey, mdiClockOutline, mdiEmailOutline, mdiFingerprint, mdiMessageTextOutline, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useAuth } from '../../hooks/useAuth'
import {
    deleteFactor,
    fetchMfaStatus,
    MfaApiError,
    mfaErrorKey,
    type FactorKind,
    type MFAConfirmResult,
} from '../../lib/mfa'
import { apiClient } from '../../api/client'
import { TotpEnrollment } from './TotpEnrollment'
import { CodeFactorEnrollment } from './CodeFactorEnrollment'
import { useFactorName } from './useFactorName'

type EnrollKind = FactorKind

const FACTOR_ICONS: Record<string, string> = {
    totp: mdiCellphoneKey,
    email: mdiEmailOutline,
    sms: mdiMessageTextOutline,
}

interface PasskeyRow {
    uid: string
    name: string
    created_at: string
    last_used_at?: string | null
}

/**
 * The two-step verification section of the Security tab (specs
 * 2026-10-01-03 to -05): the user's authenticator app, e-mail and SMS
 * factors (one of each at most) and passkeys.
 * Factors belong to the user; the organization in the URL only says whether
 * they are required and which kinds count.
 */
export function TwoStepVerificationCard({ org }: { org: string }) {
    const { t } = useTranslation('auth')
    const queryClient = useQueryClient()
    const { applyUpgradedAccessToken, user } = useAuth()
    const factorName = useFactorName()
    const [enrollKind, setEnrollKind] = useState<EnrollKind | null>(null)
    const enrollOpen = enrollKind !== null
    const [confirmed, setConfirmed] = useState<MFAConfirmResult | null>(null)
    const [deleteKind, setDeleteKind] = useState<FactorKind | null>(null)
    const [error, setError] = useState<string | null>(null)

    const queryKey = ['mfa-status', org]
    const { data: status, isLoading } = useQuery({ queryKey, queryFn: () => fetchMfaStatus(org) })
    // Passkeys answer the second step too: listed here by name.
    const { data: passkeys } = useQuery({
        queryKey: ['passkeys'],
        queryFn: async () => {
            const res = await apiClient.GET('/api/v1/auth/passkeys')
            if (res.error) throw new Error('Failed to load passkeys')
            return ((res.data as { data?: PasskeyRow[] })?.data ?? []) as PasskeyRow[]
        },
    })
    const invalidate = () => queryClient.invalidateQueries({ queryKey })

    const describeError = (err: unknown) => t(mfaErrorKey(err instanceof MfaApiError ? err.code : null))

    const deleteMutation = useMutation({
        mutationFn: (kind: FactorKind) => deleteFactor(org, kind),
        onSuccess: () => {
            setDeleteKind(null)
            invalidate()
        },
        onError: (err) => {
            setDeleteKind(null)
            setError(describeError(err))
        },
    })

    const formatDate = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
        return (value?: string | null) => (value ? fmt.format(new Date(value)) : '')
    }, [])

    const onConfirmed = (result: MFAConfirmResult) => {
        if (result.access_token) applyUpgradedAccessToken(result.access_token, result.expires_in ?? 3600)
        setConfirmed(result)
        invalidate()
    }

    const closeEnroll = (open: boolean) => {
        if (!open) {
            setEnrollKind(null)
            setConfirmed(null)
        }
    }

    const factors = status?.factors ?? []
    const enabled = factors.length > 0 || (status?.passkeys ?? 0) > 0
    const totpEnrollable = status?.enrollable_factors.includes('totp') ?? true
    // One factor per kind: a kind already held is not offered again.
    const held = new Set(factors.map((f) => f.kind))
    const enrollable = (['totp', 'email', 'sms'] as EnrollKind[]).filter((k) =>
        !held.has(k) && (k === 'totp' ? totpEnrollable : status?.enrollable_factors.includes(k)))
    // Changing factors takes an MFA-verified session, except enrolling the
    // very first one (spec 2026-10-01-03): a password alone must not let
    // anyone swap the owner's authenticator for theirs.
    const canManage = status?.management_allowed ?? false
    const canAdd = canManage || !enabled
    const verifyHref = `/mfa-verify?org=${encodeURIComponent(org)}&next=${encodeURIComponent(window.location.pathname)}`

    return (
        <div className="bg-card rounded-lg shadow-lg" data-testid="two-step-card">
            <div className="px-6 py-4 border-b border-border">
                {/* A section, not the page header: the sessions list below
                    carries the tab's one <h1>. */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <AppIcon path={SECTION_ICONS.security} size="lg" className="text-primary mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-foreground">{t('mfa.security.title')}</h2>
                            <p className="text-sm text-muted-foreground mt-1">{t('mfa.security.description')}</p>
                            {status?.required && (
                                <p className="mt-1 text-sm font-medium text-foreground" data-testid="two-step-required">
                                    {t('mfa.security.required_notice')}
                                </p>
                            )}
                        </div>
                    </div>
                    <Badge variant={enabled ? 'default' : 'outline'} data-testid="two-step-status">
                        {enabled ? t('mfa.security.status_on') : t('mfa.security.status_off')}
                    </Badge>
                </div>
                {status && enabled && !canManage && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="two-step-verify-notice">
                        <span className="text-foreground">{t('mfa.security.verify_notice')}</span>
                        {factors.length > 0 && (
                            <Button size="sm" variant="outline" asChild data-testid="two-step-verify-btn">
                                <a href={verifyHref}>{t('mfa.security.verify_button')}</a>
                            </Button>
                        )}
                    </div>
                )}
                {error && (
                    <p className="mt-2 text-sm text-destructive" data-testid="two-step-error">{error}</p>
                )}
            </div>

            <div className="p-6 space-y-6">
                <section className="space-y-3" data-testid="two-step-factors">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground">{t('mfa.security.factors_title')}</h3>
                        <div className="flex flex-wrap gap-2">
                            {enrollable.map((k) => (
                                <Button
                                    key={k}
                                    size="sm"
                                    variant={k === 'totp' ? 'default' : 'outline'}
                                    disabled={!canAdd}
                                    onClick={() => { setError(null); setEnrollKind(k) }}
                                    data-testid={`add-${k}-btn`}
                                >
                                    <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                    {t(`mfa.security.add_${k}`)}
                                </Button>
                            ))}
                        </div>
                    </div>
                    {isLoading ? null : factors.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="two-step-no-factors">{t('mfa.security.no_factors')}</p>
                    ) : (
                        <ul className="divide-y divide-border rounded-md border border-border">
                            {factors.map((f) => (
                                <li key={f.kind} className="flex items-center justify-between gap-3 p-3" data-testid={`mfa-factor-row-${f.kind}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <AppIcon path={FACTOR_ICONS[f.kind] ?? mdiCellphoneKey} size="md" className="text-primary flex-shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-foreground truncate" data-testid={`mfa-factor-name-${f.kind}`}>
                                                {factorName(f)}
                                            </div>
                                            {f.enabled_at && (
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {t('mfa.security.added', { when: formatDate(f.enabled_at) })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={!canManage}
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => { setError(null); setDeleteKind(f.kind as FactorKind) }}
                                        aria-label={t('mfa.security.delete')}
                                        title={t('mfa.security.delete')}
                                        data-testid={`delete-mfa-factor-${f.kind}`}
                                    >
                                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="space-y-2" data-testid="two-step-passkeys">
                    <h3 className="text-sm font-semibold text-foreground">{t('mfa.security.passkeys_title')}</h3>
                    {passkeys && passkeys.length > 0 && (
                        <ul className="divide-y divide-border rounded-md border border-border">
                            {passkeys.map((p) => (
                                <li key={p.uid} className="flex items-center gap-3 p-3" data-testid={`mfa-passkey-row-${p.uid}`}>
                                    <AppIcon path={mdiFingerprint} size="md" className="text-primary flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                                        <div className="flex flex-wrap items-center gap-x-4 text-xs text-muted-foreground mt-0.5">
                                            <span>{t('mfa.security.added', { when: formatDate(p.created_at) })}</span>
                                            <span className="flex items-center gap-1">
                                                <AppIcon path={mdiClockOutline} size="xs" />
                                                {p.last_used_at ? t('mfa.security.last_used', { when: formatDate(p.last_used_at) }) : t('mfa.security.never_used')}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-sm text-muted-foreground">
                        {(status?.passkeys ?? 0) > 0
                            ? t('mfa.security.passkeys_count', { count: status?.passkeys ?? 0 })
                            : t('mfa.security.passkeys_none')}{' '}
                        <Link
                            to="/$org/user/me/passkeys"
                            params={{ org }}
                            className="underline underline-offset-2 hover:text-foreground"
                            data-testid="two-step-manage-passkeys"
                        >
                            {t('mfa.security.passkeys_manage')}
                        </Link>
                    </p>
                </section>

            </div>

            <Dialog open={enrollOpen} onOpenChange={closeEnroll}>
                <DialogContent className="sm:max-w-md" data-testid={`${enrollKind ?? 'totp'}-enroll-dialog`}>
                    <DialogHeader>
                        <DialogTitle>{confirmed ? t('mfa.setup.done_title') : t(`mfa.security.dialog_title_${enrollKind ?? 'totp'}`)}</DialogTitle>
                        {!confirmed && <DialogDescription>{t('mfa.security.description')}</DialogDescription>}
                    </DialogHeader>
                    {confirmed ? (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground" data-testid="mfa-enroll-done">{t('mfa.setup.done_description')}</p>
                            <Button className="w-full" onClick={() => closeEnroll(false)} data-testid="totp-enroll-close">
                                {t('mfa.security.close')}
                            </Button>
                        </div>
                    ) : (
                        enrollKind === 'totp' ? <TotpEnrollment org={org} onConfirmed={onConfirmed} />
                            : enrollKind ? <CodeFactorEnrollment org={org} kind={enrollKind} email={user?.email} onConfirmed={onConfirmed} />
                            : null
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteKind} onOpenChange={(open) => { if (!open) setDeleteKind(null) }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('mfa.security.delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('mfa.security.delete_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-mfa-factor-cancel">{t('mfa.security.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); if (deleteKind) deleteMutation.mutate(deleteKind) }}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="delete-mfa-factor-confirm"
                        >
                            {t('mfa.security.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </div>
    )
}
